import catalogo from '../data/textosPadrao.json';

/**
 * Textos das telas (`VAR3`). O padrão de cada chave vem de `textosPadrao.json`,
 * que acompanha o aplicativo; o que o admin alterou chega do servidor
 * (`GET /api/v1/textos`) e passa por cima. As cenas pedem o texto pela chave
 * com `t(chave)` e nunca escrevem a frase.
 *
 * `{nome}` no texto é uma variável preenchida na hora (`t(chave, { nome })`).
 * Um texto alterado só vale se mantiver as mesmas variáveis do padrão: sem
 * isso a tela mostraria `{ordem}` cru ou perderia o número.
 */

const PADRAO = new Map(catalogo.textos.map((item) => [item.chave, item.padrao]));
const CHAVE_DO_CACHE = 'fiat-arcade:textos:v1';
let vigentes = new Map();

/** Variáveis `{assim}` de um texto, sem repetição e em ordem alfabética. */
export function variaveisDe (texto) {
    return [...new Set(String(texto ?? '').match(/\{[a-z_]+\}/g) ?? [])].sort();
}

/** Um texto alterado vale quando a chave existe, há conteúdo e as variáveis são as do padrão. */
export function textoValido (chave, valor) {
    if (!PADRAO.has(chave) || typeof valor !== 'string' || valor.trim() === '') return false;
    return variaveisDe(valor).join() === variaveisDe(PADRAO.get(chave)).join();
}

/** Texto vigente da chave. Chave desconhecida devolve a própria chave e avisa no console. */
export function t (chave, variaveis = null) {
    const modelo = vigentes.get(chave) ?? PADRAO.get(chave);
    if (modelo === undefined) {
        console.warn(`[Textos] chave desconhecida: ${chave}`);
        return chave;
    }
    if (!variaveis) return modelo;
    return modelo.replace(/\{([a-z_]+)\}/g, (marca, nome) => (nome in variaveis ? String(variaveis[nome] ?? '') : marca));
}

/**
 * Troca o conjunto de textos alterados pelo recebido; o que não vier volta ao
 * padrão. Entradas inválidas são ignoradas. Devolve quantas foram aceitas.
 */
export function aplicarTextos (alterados) {
    const aceitos = Object.entries(alterados ?? {}).filter(([chave, valor]) => textoValido(chave, valor));
    vigentes = new Map(aceitos);
    return aceitos.length;
}

/** Volta todos os textos ao padrão do aplicativo. */
export function restaurarTextos () {
    vigentes = new Map();
}

/** Chaves conhecidas, na ordem do catálogo. */
export function chavesDeTexto () {
    return [...PADRAO.keys()];
}

/** Aplica o que ficou guardado da última leitura — o tablet pode ligar sem rede. */
export function aplicarTextosGuardados (armazenamento) {
    try {
        return aplicarTextos(JSON.parse(armazenamento?.getItem(CHAVE_DO_CACHE) ?? '{}'));
    } catch {
        return 0;
    }
}

/**
 * Lê os textos alterados no servidor e aplica. Rota pública: a tela de login
 * também tem textos. Falha de rede ou resposta estranha mantém o que já valia
 * e devolve `false` — texto nunca impede o aplicativo de abrir.
 */
export async function carregarTextos ({ baseUrl, buscar = globalThis.fetch, armazenamento = null, tempoLimiteMs = 4000 } = {}) {
    const controle = new AbortController();
    const relogio = setTimeout(() => controle.abort(), tempoLimiteMs);
    try {
        const resposta = await buscar(`${String(baseUrl ?? '').replace(/\/+$/, '')}/api/v1/textos`, {
            headers: { Accept: 'application/json' }, cache: 'no-store', signal: controle.signal
        });
        if (!resposta.ok) return false;
        const corpo = await resposta.json();
        if (!corpo || typeof corpo.textos !== 'object' || corpo.textos === null || Array.isArray(corpo.textos)) return false;
        aplicarTextos(corpo.textos);
        try { armazenamento?.setItem(CHAVE_DO_CACHE, JSON.stringify(Object.fromEntries(vigentes))); } catch { /* sem storage */ }
        return true;
    } catch {
        return false;
    } finally {
        clearTimeout(relogio);
    }
}
